<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import UploadIcon from '@primeicons/vue/upload';
import { Section, Stack, Typography } from '@apparatix/library/core';
import type { OfferSubmissionDocument, OfferSubmissionEligibility, OfferSubmissionMode } from '@apparatix/types/Documents';
import SubmitOfferModal from './SubmitOfferModal.vue';
import { sectionHeading, statusCopy, submittedStatusCopy } from './offerSubmissionCopy';

/**
 * Offer / Contract card for the Sales Documents flyout: where the document
 * sits in the offer → contract lifecycle, plus the Submit Offer / Submit
 * Contract entry points the current user is allowed to use.
 */
type Props = {
  document: OfferSubmissionDocument;
  offerSubmission: OfferSubmissionEligibility;
};

type Emits = {
  (e: 'submitted', mode: OfferSubmissionMode): void;
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const heading = computed(() => sectionHeading(props.offerSubmission.isEssentials));

// The stage jobs run on a queue, so the server status still reads "ready"
// right after a submit (and the flyout would remount this section if it
// refetched). Show what was just submitted until the document changes or a
// later load reports a status past "ready".
const justSubmitted = ref<OfferSubmissionMode | null>(null);

watch(() => props.document.proposalId, () => justSubmitted.value = null);
watch(() => props.offerSubmission.status, status => {
  if (status !== 'ready') {
    justSubmitted.value = null;
  }
});

const statusMeta = computed(() => (justSubmitted.value
  ? submittedStatusCopy(justSubmitted.value, props.offerSubmission.isEssentials)
  : statusCopy(props.offerSubmission.status, props.offerSubmission.isEssentials)));

const showButtons = computed(() => props.offerSubmission.isDocumentEligible
  && justSubmitted.value !== 'contract'
  && (props.offerSubmission.canSubmitOffer || props.offerSubmission.canSubmitContract));
const showBothButtons = computed(() => props.offerSubmission.canSubmitOffer && props.offerSubmission.canSubmitContract);

const modalVisible = ref(false);
const modalMode = ref<OfferSubmissionMode>('offer');

const openModal = (mode: OfferSubmissionMode) => {
  modalMode.value = mode;
  modalVisible.value = true;
};

const onSubmitted = () => {
  modalVisible.value = false;
  justSubmitted.value = modalMode.value;
  emit('submitted', modalMode.value);
};
</script>

<template>
  <div class="w-full">
    <Section :footer="false">
      <template #title>
        <div class="flex flex-wrap items-center gap-3">
          <Typography variant="section-heading">
            {{ heading }}
          </Typography>
          <Tag :value="statusMeta.label" :severity="statusMeta.severity" rounded />
        </div>
      </template>
      <Stack :gap="4">
        <Typography variant="body" muted>
          {{ statusMeta.copy }}
        </Typography>
        <div v-if="showButtons" class="flex flex-wrap gap-2">
          <Button
            v-if="props.offerSubmission.canSubmitOffer"
            @click="openModal('offer')"
          >
            <UploadIcon />
            Submit Offer
          </Button>
          <Button
            v-if="props.offerSubmission.canSubmitContract"
            :severity="showBothButtons ? 'secondary' : undefined"
            @click="openModal('contract')"
          >
            <UploadIcon />
            Submit Contract
          </Button>
        </div>
      </Stack>
    </Section>
    <SubmitOfferModal
      :visible="modalVisible"
      :document="props.document"
      :mode="modalMode"
      :is-essentials="props.offerSubmission.isEssentials"
      @close="modalVisible = false"
      @submitted="onSubmitted"
    />
  </div>
</template>
